import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { AlertBox } from '@/common/AlertBox';
import type { InfoSectionData } from '@/common/InfoSection';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { WBSButton } from '@/common/WBSButton';
import { ProfilePage } from '@/profile/ProfilePage';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useLanguageLabels } from '~/hooks/useLanguageLabels';
import { apiLanguageToLocale } from '~/utils/languageFormat';

type StudentProfile = components['schemas']['StudentProfile'];
type ApiLanguage = components['schemas']['Language'];

export const Route = createFileRoute('/_app/_users/members/$memberId/profile/')(
  {
    component: MemberProfileRoute,
  },
);

// Coerce nullable/number API values into the plain strings InfoSection renders.
function text(value: string | number | null | undefined): string {
  return value == null ? '' : String(value);
}

export default function MemberProfileRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { memberId } = Route.useParams();
  const { isStudent } = useCurrentUser();
  const languageLabels = useLanguageLabels();

  const memberIdNum = Number.parseInt(memberId, 10);

  const {
    data: profile,
    isLoading,
    error,
  } = api.getStudentProfile.useQuery(
    { path: { student_id: memberIdNum } },
    { enabled: Number.isFinite(memberIdNum) },
  );

  if (isLoading) {
    return <LoadingSpinner centered />;
  }

  if (error || !profile) {
    return (
      <AlertBox variant="error">
        {/* Translation Needed */}
        Failed to load profile.
      </AlertBox>
    );
  }

  const sections = buildSections(profile, t, languageLabels);

  return (
    <ProfilePage
      sections={sections}
      headerActions={
        <WBSButton
          wide
          alternate
          onClick={() =>
            navigate({
              to: '/members/$memberId/profile/edit/',
              params: { memberId },
            })
          }
        >
          {isStudent
            ? t('actions.edit-profile')
            : t('actions.edit-student-profile')}
        </WBSButton>
      }
    />
  );
}

function buildSections(
  profile: StudentProfile,
  t: TFunction,
  languageLabels: Record<string, string>,
): InfoSectionData[] {
  const languageLabel = (language: ApiLanguage | null): string =>
    language ? languageLabels[apiLanguageToLocale(language)] : '';

  return [
    {
      title: t('wbs.details'),
      rows: [
        {
          label: t('wbs.first-name'),
          value: text(profile.first_name),
          type: 'text',
        },
        {
          label: t('wbs.last-name'),
          value: text(profile.last_name),
          type: 'text',
        },
        {
          label: t('wbs.in-what-year-were'),
          value: text(profile.birth_year),
          type: 'text',
        },
        {
          // TODO: Localize/capitalize gender ("male" | "female" | "unknown").
          // Translation Needed
          label: t('wbs.gender'),
          value: text(profile.gender),
          type: 'text',
        },
        {
          // TODO (follow-up): localize the raw ISO 3166-1 alpha-2 country code
          // to a full, localized country name.
          label: t('wbs.country'),
          value: text(profile.country),
          type: 'text',
        },
        { label: t('wbs.city'), value: text(profile.city), type: 'text' },
        {
          label: t('wbs.state-province'),
          value: text(profile.region),
          type: 'text',
        },
        {
          // Translation Needed
          label: 'I prefer to learn in',
          value: languageLabel(profile.learning_language),
          type: 'text',
        },
        {
          // TODO (follow-up): map the raw profile type (WEB_STUDENT /
          // POSTAL_STUDENT / LITEAPP_STUDENT) to a localized delivery label.
          label: t('lessons.how-do-you-prefer'),
          value: text(profile.profile_type),
          type: 'text',
        },
        {
          label: t('lessons.how-did-you-hear'),
          value: text(profile.how_did_you_hear),
          type: 'text',
        },
        {
          label: t('wbs.what-is-your-religion'),
          value: text(profile.religion),
          type: 'text',
        },
        {
          label: t('wbs.what-is-your-marital'),
          value: text(profile.marital_status),
          type: 'text',
        },
        // Should this be here? Not seeing it from SH viewing Student
        {
          label: t('wbs.primary-site-language'),
          value: languageLabel(profile.site_language),
          type: 'text',
        },
      ],
    },
    // Should this be here? Not seeing it from SH viewing Student
    {
      title: t('profile.social-settings'),
      rows: [
        {
          label: t('profile.do-you-want-your'),
          value: profile.profile_is_public ? t('wbs.yes') : t('wbs.no'),
          type: 'text',
        },
      ],
    },
    // Should this be here? Not seeing it from SH viewing Student
    {
      title: t('lessons.study-helper-preferences'),
      rows: [
        {
          // TODO: Localize study-helper gender preference. API returns the raw
          // enum ("no_pref" | "same" | null). Translation Needed.
          label: t('lessons.do-you-have-a'),
          value: text(profile.gender_of_study_helper),
          type: 'text',
        },
        {
          // board_listing is a legacy raw string; treat any value as opted-in.
          label: t('lessons.do-you-want-to'),
          value: profile.board_listing ? t('wbs.yes') : t('wbs.no'),
          type: 'text',
        },
      ],
    },
    {
      title: t('wbs.spiritual-journey'),
      rows: [
        {
          label: t('wbs.do-you-strongly-believe'),
          value: text(profile.existence),
          type: 'text',
        },
        {
          label: t('wbs.would-you-like-to'),
          value: text(profile.existence_explain),
          type: 'text',
        },
        {
          label: t('wbs.do-you-believe-the'),
          value: text(profile.bible_is_word),
          type: 'text',
        },
        {
          label: t('wbs.i-believe-that-jesus'),
          value: text(profile.jesus_is),
          type: 'text',
        },
        {
          label: t('wbs.please-select-the-answer'),
          value: text(profile.describes_you),
          type: 'text',
        },
        {
          label: t('wbs.you-may-explain-your'),
          value: text(profile.describes_you_explain),
          type: 'text',
        },
        {
          label: t('groups.are-you-a-part'),
          value: text(profile.community),
          type: 'text',
        },
        {
          label: t('wbs.if-yes-what-is'),
          value: text(profile.church_name),
          type: 'text',
        },
        {
          label: t('wbs.have-you-been-baptized'),
          value: text(profile.baptize_status),
          type: 'text',
        },
        {
          // Translation Needed
          label: 'What is the PRIMARY reason you were baptized?',
          value: text(profile.baptized_reason),
          type: 'text',
        },
        {
          label: t('wbs.i-believe-i-was'),
          value: text(profile.believe_i_was),
          type: 'text',
        },
        {
          label: t('wbs.please-feel-free-to'),
          value: text(profile.believe_i_was_explain),
          type: 'text',
        },
        {
          label: t('wbs.in-baptism-we-are'),
          value: text(profile.in_baptism),
          type: 'text',
        },
        {
          label: t('wbs.i-want-to-die'),
          value: text(profile.want_baptism_explain),
          type: 'text',
        },
        {
          label: t('lessons.do-you-want-your'),
          value: text(profile.baptize_request),
          type: 'text',
        },
        {
          label: t('wbs.would-you-like-a'),
          value: text(profile.send_bible),
          type: 'text',
        },
      ],
    },
  ];
}
